import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, X, Info } from 'lucide-react';
import { PDMeasurementLib, PDMeasurementResult, PDMeasurementState } from '../lib/tryon/PDMeasurementLib';
import './PDMeasurementPopup.css';

interface PDMeasurementPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (result: PDMeasurementResult) => void;
}

export function PDMeasurementPopup({ isOpen, onClose, onComplete }: PDMeasurementPopupProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pdLibRef = useRef<PDMeasurementLib | null>(null);
  const [status, setStatus] = useState<string>('Initializing camera...');
  const [pdState, setPdState] = useState<PDMeasurementState | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(true);
  
  const [step, setStep] = useState<'selection' | 'measuring'>('selection');
  const [pdType, setPdType] = useState<'single' | 'dual'>('single');

  // Enumerate cameras
  useEffect(() => {
    if (isOpen && step === 'measuring') {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        setDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedDeviceId(videoDevices[0].deviceId);
        }
      }).catch(err => {
        console.error("Error enumerating devices:", err);
      });
    }
  }, [isOpen, step]);

  // Start video stream when device selected
  useEffect(() => {
    if (!isOpen || step !== 'measuring' || !selectedDeviceId) return;
    
    let activeStream: MediaStream | null = null;
    
    const startStream = async () => {
      try {
        if (stream) {
          stream.getTracks().forEach(t => t.stop());
        }
        activeStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          }
        });
        setStream(activeStream);
        if (videoRef.current) {
          videoRef.current.srcObject = activeStream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play().then(() => {
              startML();
            });
          };
        }
      } catch (err: any) {
        setError(`Camera error: ${err.message}`);
      }
    };

    startStream();

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach(t => t.stop());
      }
      stopML();
    };
  }, [isOpen, step, selectedDeviceId]);

  const startML = () => {
    setStatus('Loading AI Models...');
    setError(null);
    if (!videoRef.current) return;
    
    if (!pdLibRef.current) {
      pdLibRef.current = new PDMeasurementLib(videoRef.current, {
        pdType,
        onStatus: (newStatus: string) => setStatus(newStatus),
          onStateChange: (state) => setPdState(state),
        onDetected: (result: PDMeasurementResult) => onComplete(result),
        onError: (err: string) => setError("ML Error: " + err)
      });
    }
    
    pdLibRef.current.start().catch((err: any) => {
      console.error(err);
      setError("ML Error: " + err.message);
    });
  };

  const stopML = () => {
    if (pdLibRef.current) {
      pdLibRef.current.stop();
      pdLibRef.current = null;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setStep('selection');
      setPdType('single');
      if (stream) {
        stream.getTracks().forEach(t => t.stop());
        setStream(null);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className={`pd-popup-overlay ${step === 'measuring' ? 'pd-overlay-measuring' : ''}`} onClick={(e) => e.stopPropagation()}>
      <div className={`pd-popup-content ${step === 'measuring' ? 'pd-content-measuring' : ''}`}>
        <button className="pd-popup-close-btn" onClick={onClose} title="Close">
          <X size={20} />
        </button>

        {step === 'selection' ? (
          <div className="pd-selection-container">
            <div className="pd-popup-header">
              <h2 className="pd-popup-title">Select Your Goal</h2>
              <p className="pd-popup-subtitle" style={{marginTop: '8px'}}>Choose the measurement type required.</p>
            </div>
            
            <div className="pd-selection-body" style={{padding: '24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '16px'}}>
                <label 
                  style={{
                    display: 'flex', flexDirection: 'column', padding: '16px', 
                    border: pdType === 'single' ? '2px solid #0891b2' : '2px solid #e2e8f0',
                    backgroundColor: pdType === 'single' ? '#ecfeff' : 'white',
                    borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s'
                  }}
                >
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                    <span style={{fontSize: '12px', fontWeight: 600, padding: '4px 8px', backgroundColor: '#f1f5f9', borderRadius: '6px', color: '#475569'}}>Standard</span>
                    <input type="radio" name="pdType" value="single" checked={pdType === 'single'} onChange={() => setPdType('single')} style={{width: '20px', height: '20px', accentColor: '#0891b2'}} />
                  </div>
                  <h3 style={{fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0', color: '#1e293b'}}>Single PD (Binocular)</h3>
                  <p style={{fontSize: '13px', margin: 0, color: '#475569'}}>Measures the total distance from the center of one pupil directly to the center of the other pupil.</p>
                </label>
                
                <label 
                  style={{
                    display: 'flex', flexDirection: 'column', padding: '16px', 
                    border: pdType === 'dual' ? '2px solid #0891b2' : '2px solid #e2e8f0',
                    backgroundColor: pdType === 'dual' ? '#ecfeff' : 'white',
                    borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s'
                  }}
                >
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                    <span style={{fontSize: '12px', fontWeight: 600, padding: '4px 8px', backgroundColor: '#f1f5f9', borderRadius: '6px', color: '#475569'}}>High Precision</span>
                    <input type="radio" name="pdType" value="dual" checked={pdType === 'dual'} onChange={() => setPdType('dual')} style={{width: '20px', height: '20px', accentColor: '#0891b2'}} />
                  </div>
                  <h3 style={{fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0', color: '#1e293b'}}>Dual PD (Monocular)</h3>
                  <p style={{fontSize: '13px', margin: 0, color: '#475569'}}>Measures each eye individually from the center of your pupil to the bridge of your nose.</p>
                </label>
              </div>
              
              <button 
                onClick={() => setStep('measuring')}
                style={{
                  width: '100%', marginTop: '24px', padding: '12px', 
                  backgroundColor: '#0891b2', color: 'white', fontWeight: 'bold', 
                  border: 'none', borderRadius: '8px', cursor: 'pointer'
                }}
              >
                Proceed to Measurement
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="pd-popup-header pd-header-measuring">
              <h2 className="pd-popup-title text-white">Calculate Pupillary Distance (PD)</h2>
              <p className="pd-popup-subtitle text-white/70">Place a credit card against your forehead.</p>
            </div>

            <div className="pd-popup-body pd-body-measuring">
              {devices.length > 1 && (
                <div className="pd-camera-selector">
                  <Camera size={16} />
                  <select 
                    value={selectedDeviceId} 
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                    className="pd-camera-select"
                  >
                    {devices.map(d => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Camera ${d.deviceId.slice(0, 5)}...`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="pd-video-container">
                {error ? (
                  <div className="pd-error">
                    <p><strong>Error</strong></p>
                    <p>{error}</p>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      className="pd-video"
                      playsInline
                      autoPlay
                      muted
                    />
                    
                                  {/* Face Alignment UI */}
              {pdState && pdState.phase === 'calibration' && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 10 }}>
                  <div style={{ position: 'relative', width: '208px', height: '208px', maxWidth: '70vw', maxHeight: '70vw', borderRadius: '16px', border: `4px solid ${pdState.isFaceStraight ? '#34d399' : '#fbbf24'}`, backgroundColor: 'rgba(255,255,255,0.05)', transition: 'border-color 0.3s' }}>
                    <div style={{ position: 'absolute', top: '32px', left: '32px', right: '32px', bottom: '32px', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.8)' }}></div>
                    <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '64px', height: '64px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.7)' }}></div>
                    <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.7)' }}></div>
                  </div>
                </div>
              )}

              <div className="pd-status-overlay">
                      <div className="pd-status-box">
                        <div className="pd-spinner"></div>
                        <span className="pd-status-text">{status}</span>
                      </div>
                    </div>
                    <div className="pd-info-bottom-right">
                      <button className="pd-info-button" onClick={() => setShowTooltip(!showTooltip)}>
                        <Info size={20} color="#60a5fa" />
                      </button>
                      {showTooltip && (
                        <div className="pd-tooltip-bottom">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <p style={{ fontWeight: 600, margin: 0 }}>How to measure:</p>
                            <button type="button" style={{ background: 'none', border: 'none', padding: 0 }} onClick={() => setShowTooltip(false)}><X size={16} color="white" style={{ cursor: 'pointer', opacity: 0.7 }} /></button>
                          </div>
                          <ul style={{ paddingLeft: '16px', margin: 0, fontSize: '12px', opacity: 0.9 }}>
                            <li>Hold a standard credit/debit card flat against your forehead.</li>
                            <li>Ensure the top edge is visible and level.</li>
                            <li>Look straight into the camera and hold still.</li>
                            <li>The system will detect the card and measure your PD automatically.</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
